import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  HStack,
  VStack,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Input,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useClipboard,
} from '@chakra-ui/react';
import { LuTrash2, LuCopy, LuUserPlus, LuRefreshCcw } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  listWorkspaceMembers,
  removeWorkspaceMember,
  createInvitation,
  listInvitations,
  revokeInvitation,
} from '../../api/endpointsBackend';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useIsPhone from '../../hooks/useIsPhone';
import { DesktopNoteCard } from '../phone/PhoneReadOnlyParts';
import PageHeader from '../shared/PageHeader';
import { cardSurface } from '../shared/Card';

const ROLES_INVITABLE = ['Co-Owner', 'Member', 'Advisor', 'Viewer'];

const RoleBadge = ({ role }) => {
  const { t } = useTranslation('workspaces');
  const map = {
    Owner: 'green',
    'Co-Owner': 'blue',
    Member: 'purple',
    Advisor: 'orange',
    Viewer: 'gray',
  };
  return (
    <Badge colorScheme={map[role] || 'gray'} textTransform="none" fontWeight={500}>
      {t(`roles.${role}`, role)}
    </Badge>
  );
};

const InviteTokenModal = ({ token, isOpen, onClose }) => {
  const { t } = useTranslation('workspaces');
  const { hasCopied, onCopy } = useClipboard(
    `${window.location.origin}/invitations/accept?token=${token || ''}`
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('members.inviteModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing="14px">
            <Text fontSize="md">{t('members.inviteModal.body')}</Text>
            <Box
              p="10px"
              border="1px solid"
              borderColor="redesign.border"
              borderRadius="8px"
              fontFamily="monospace"
              fontSize="sm"
              wordBreak="break-all"
              bg="redesign.bg.card"
            >
              {window.location.origin}/invitations/accept?token={token}
            </Box>
            <Text fontSize="sm" color="redesign.text.muted">
              {t('members.inviteModal.expiryNote')}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>
            {t('members.inviteModal.close')}
          </Button>
          <Button colorScheme="blue" leftIcon={<LuCopy />} onClick={onCopy}>
            {hasCopied ? t('members.inviteModal.copied') : t('members.inviteModal.copy')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const MembersPageDesktop = () => {
  const { current, currentId, can } = useWorkspace();
  const { t } = useTranslation('workspaces');
  const toast = useToast();
  const navigate = useNavigate();
  // MI-345: the roster is the owner's and co-owners' (`member.read`). Anyone
  // else who lands here (switching into a shared space while on this page, or
  // by its link) gets a note instead of a request the server refuses.
  const canReadMembers = can('member.read');

  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Member');
  const [createdToken, setCreatedToken] = useState(null);
  const tokenModal = useDisclosure();

  const isPrivileged =
    current?.workspace_role === 'Owner' || current?.workspace_role === 'Co-Owner';

  const load = useCallback(async () => {
    if (!currentId || !canReadMembers) return;
    setLoading(true);
    try {
      const [ms, invs] = await Promise.all([
        listWorkspaceMembers(currentId),
        isPrivileged ? listInvitations(currentId) : Promise.resolve([]),
      ]);
      setMembers(ms);
      setInvitations(invs);
    } catch (err) {
      toast({
        status: 'error',
        title: t('members.toasts.loadFailed'),
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setLoading(false);
    }
  }, [currentId, canReadMembers, isPrivileged, toast, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const result = await createInvitation(currentId, inviteEmail.trim(), inviteRole);
      setCreatedToken(result.token);
      tokenModal.onOpen();
      setInviteEmail('');
      await load();
    } catch (err) {
      toast({
        status: 'error',
        title: t('members.toasts.inviteFailed'),
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm(t('members.confirmRemove'))) return;
    try {
      await removeWorkspaceMember(currentId, userId);
      await load();
    } catch (err) {
      toast({
        status: 'error',
        title: t('members.toasts.removeFailed'),
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const handleRevoke = async (invitationId) => {
    try {
      await revokeInvitation(currentId, invitationId);
      await load();
    } catch (err) {
      toast({
        status: 'error',
        title: t('members.toasts.revokeFailed'),
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  if (!current) {
    return (
      <Container maxW="container.md" py="40px">
        <Spinner />
      </Container>
    );
  }

  const subtitle = (
    <Box>
      <Text color="redesign.text.muted" fontSize="md">
        {t('members.subtitleYouAre', {
          name: current.name,
          role: t(`roles.${current.workspace_role}`, current.workspace_role),
        })}
      </Text>
    </Box>
  );

  if (!canReadMembers) {
    return (
      <>
      <PageHeader title={t('members.pageTitle')} />
      <Container maxW="container.lg" py="32px">
        <Stack spacing="32px">
          {subtitle}
          <Box {...cardSurface} p="20px">
            <Text fontSize="md" color="redesign.heading">
              {t('members.noAccess')}
            </Text>
            <Button mt="14px" size="sm" variant="outline" onClick={() => navigate('/dashboard')}>
              {t('members.goToDashboard')}
            </Button>
          </Box>
        </Stack>
      </Container>
      </>
    );
  }

  return (
    <>
    <PageHeader title={t('members.pageTitle')} />
    <Container maxW="container.lg" py="32px">
      <Stack spacing="32px">
        {subtitle}

        {isPrivileged && (
          <Box
            {...cardSurface}
            p="20px"
          >
            <Heading size="sm" color="redesign.heading" mb="14px">
              {t('members.inviteHeading')}
            </Heading>
            <form onSubmit={handleInvite}>
              <HStack spacing="10px" align="flex-end">
                <FormControl flex={1}>
                  <FormLabel fontSize="sm" mb="4px">
                    {t('members.fields.email')}
                  </FormLabel>
                  <Input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={t('members.fields.emailPlaceholder')}
                    size="sm"
                  />
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel fontSize="sm" mb="4px">
                    {t('members.fields.role')}
                  </FormLabel>
                  <Select
                    size="sm"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    {ROLES_INVITABLE.map((r) => (
                      <option key={r} value={r}>
                        {t(`roles.${r}`, r)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="sm"
                  leftIcon={<LuUserPlus />}
                >
                  {t('members.inviteButton')}
                </Button>
              </HStack>
            </form>
          </Box>
        )}

        <Box
          {...cardSurface}
        >
          <HStack justify="space-between" px="20px" py="16px">
            <Heading size="sm" color="redesign.heading">
              {t('members.membersHeading')} ({members.length})
            </Heading>
            <IconButton
              icon={<LuRefreshCcw />}
              aria-label={t('members.actions.refresh')}
              size="xs"
              variant="ghost"
              onClick={load}
              isLoading={loading}
            />
          </HStack>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>{t('members.table.name')}</Th>
                <Th>{t('members.table.email')}</Th>
                <Th>{t('members.table.role')}</Th>
                <Th>{t('members.table.since')}</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {members.map((m) => (
                <Tr key={m.id}>
                  <Td>{m.full_name || '—'}</Td>
                  <Td fontSize="sm" color="redesign.text.muted">
                    {m.email}
                  </Td>
                  <Td>
                    <RoleBadge role={m.workspace_role} />
                  </Td>
                  <Td fontSize="sm" color="redesign.text.muted">
                    {new Date(m.accepted_at).toLocaleDateString()}
                  </Td>
                  <Td textAlign="right">
                    {isPrivileged && m.workspace_role !== 'Owner' && (
                      <IconButton
                        icon={<LuTrash2 />}
                        aria-label={t('members.actions.remove')}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleRemove(m.user_id)}
                      />
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {isPrivileged && (
          <Box
            {...cardSurface}
          >
            <HStack justify="space-between" px="20px" py="16px">
              <Heading size="sm" color="redesign.heading">
                {t('members.pendingInvitationsHeading')} ({invitations.length})
              </Heading>
            </HStack>
            {invitations.length === 0 ? (
              <Text px="20px" pb="20px" fontSize="md" color="redesign.text.muted">
                {t('members.noPendingInvitations')}
              </Text>
            ) : (
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t('members.table.email')}</Th>
                    <Th>{t('members.table.role')}</Th>
                    <Th>{t('members.table.expires')}</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {invitations.map((inv) => (
                    <Tr key={inv.id}>
                      <Td>{inv.invited_email}</Td>
                      <Td>
                        <RoleBadge role={inv.workspace_role} />
                      </Td>
                      <Td fontSize="sm" color="redesign.text.muted">
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </Td>
                      <Td textAlign="right">
                        <IconButton
                          icon={<LuTrash2 />}
                          aria-label={t('members.actions.revoke')}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleRevoke(inv.id)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </Box>
        )}
      </Stack>

      <InviteTokenModal
        token={createdToken}
        isOpen={tokenModal.isOpen}
        onClose={() => {
          tokenModal.onClose();
          setCreatedToken(null);
        }}
      />
    </Container>
    </>
  );
};

// Members are managed on a computer: a deep link on the phone gets the note,
// in the More sheet's words, and a way home instead.
const MembersPhone = () => {
  const { t } = useTranslation(['navigation', 'common']);
  const navigate = useNavigate();
  return (
    <Box pt="24px">
      <DesktopNoteCard mt="0" message={t('more.membersNote')} />
      <Box px="16px" pt="20px">
        <Button variant="phonePrimary" w="100%" onClick={() => navigate('/dashboard')}>
          {t('common:phone.goHome')}
        </Button>
      </Box>
    </Box>
  );
};

// The desktop body stays as it was.
const MembersPage = (props) => (
  useIsPhone() ? <MembersPhone /> : <MembersPageDesktop {...props} />
);

export default MembersPage;
